// The Priority of Option Select Item cards, at runtime: which cards of each
// choice variable are on screen, and the decision in ./preselect.ts run again
// whenever that set changes.
//
// The registry is per pipe and holds mounted cards only. A card hidden by a
// visibility rule — its own or an ancestor's — is unmounted by RenderComponents,
// so the registry is exactly "the cards the visitor can see".

import { nextTick, onBeforeUnmount, onMounted, watch } from 'vue'
import type { OfferGroupDataDto } from '@api/types.gen'
import type { PipeInterface } from '@render/composables/usePipe'
import { emitPayment, PAYMENT_EVENTS } from '@render/composables/usePaymentEvents'
import { valuesMatch } from './optionValue'
import {
  planPreselect,
  preselectWinner,
  usesPriorities,
  type AutoPick,
  type PreselectCandidate,
} from './preselect'

interface Member {
  candidate: () => PreselectCandidate
  el: () => Element | null
}

interface Registry {
  groups: Map<string, Map<symbol, Member>>
  autoPicks: Map<string, AutoPick>
  pending: Set<string>
}

const registries = new WeakMap<PipeInterface, Registry>()

function registryOf(pipe: PipeInterface): Registry {
  let registry = registries.get(pipe)
  if (!registry) {
    registry = { groups: new Map(), autoPicks: new Map(), pending: new Set() }
    registries.set(pipe, registry)
  }
  return registry
}

// The plan choice: what Offer Select writes, and what checkout, Pipeorders and
// the payment events read.
const SELECTED_OFFER_KEY = 'global.selectedOfferGroup'
const SELECTED_OFFER_IS_DEFAULT = 'var.global.selectedOfferGroupIsDefault'

const isOfferGroup = (v: unknown): v is OfferGroupDataDto =>
  !!v && typeof v === 'object' && (v as { type?: unknown }).type === 'offer_group_value'

function inPageOrder(members: Member[]): Member[] {
  return [...members].sort((a, b) => {
    const ea = a.el()
    const eb = b.el()
    if (!ea || !eb || ea === eb) return 0
    return ea.compareDocumentPosition(eb) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
  })
}

function membersOf(registry: Registry, key: string): Member[] {
  const group = registry.groups.get(key)
  return group ? inPageOrder([...group.values()]) : []
}

function reconcile(pipe: PipeInterface, registry: Registry, key: string) {
  const members = membersOf(registry, key)
  if (members.length === 0) return
  const candidates = members.map((m) => m.candidate())
  const index = planPreselect(
    candidates,
    pipe.getData(`var.${key}`),
    registry.autoPicks.get(key),
    valuesMatch,
  )
  if (index === -1) return
  const { value } = candidates[index]!
  pipe.setData(`var.${key}`, value)
  registry.autoPicks.set(key, { value })
  if (key === SELECTED_OFFER_KEY && isOfferGroup(value)) {
    // What Offer Select does when it applies its default option: Pipeorders
    // turns plan-selected into the order's PLAN_SELECTED status.
    pipe.setData(SELECTED_OFFER_IS_DEFAULT, true)
    emitPayment({ pipe, event: PAYMENT_EVENTS.planSelected, offer: value })
    emitPayment({ pipe, event: PAYMENT_EVENTS.trackingPlanSelectedSlimPayload, offer: value })
  }
}

// Decided only once the page is ready, and after the render that follows: the
// visitor's saved answers are merged in after the first render, and an
// abandoned-cart link restores its order later still. Until then the cards on
// screen are the ones the rules pick for unset values — an unset number is
// "less than 30" — and picking among them stores an offer the visitor never sees.
function schedule(pipe: PipeInterface, registry: Registry, key: string) {
  if (!key || registry.pending.has(key)) return
  registry.pending.add(key)
  void pipe
    .whenReady()
    .catch(() => {})
    .then(() => nextTick())
    .then(() => {
      registry.pending.delete(key)
      reconcile(pipe, registry, key)
    })
}

export interface PreselectGroup {
  /** True while a card of this item's group on screen has a priority that counts. */
  usesPriorities: () => boolean
  /** Records a pick made by the visitor, which the group then keeps while it is on screen. */
  picked: (value?: unknown) => void
}

/**
 * Joins the item to its group while mounted. Re-decides the group when the
 * item comes or goes, or its variable, value, priority, Start selected or mode
 * changes — never when the stored value alone changes, so a visitor clearing
 * their own pick is not refilled while they are on the page.
 */
export function usePreselect(
  pipe: PipeInterface,
  options: {
    varKey: () => string
    candidate: () => PreselectCandidate
    el: () => Element | null
  },
): PreselectGroup {
  const id = Symbol('OptionSelectItem')
  const member: Member = { candidate: options.candidate, el: options.el }
  let joinedKey = ''
  let mounted = false

  function join(key: string) {
    if (!key) return
    const registry = registryOf(pipe)
    let group = registry.groups.get(key)
    if (!group) {
      group = new Map()
      registry.groups.set(key, group)
    }
    group.set(id, member)
    joinedKey = key
    schedule(pipe, registry, key)
  }

  function leave() {
    const key = joinedKey
    if (!key) return
    const registry = registryOf(pipe)
    const group = registry.groups.get(key)
    group?.delete(id)
    if (group?.size === 0) registry.groups.delete(key)
    joinedKey = ''
    schedule(pipe, registry, key)
  }

  onMounted(() => {
    mounted = true
    join(options.varKey())
  })

  onBeforeUnmount(() => {
    mounted = false
    leave()
  })

  watch(options.varKey, (key) => {
    if (!mounted) return
    leave()
    join(key)
  })

  watch(
    () => {
      const c = options.candidate()
      return [c.value, c.priority, c.startSelected, c.multi]
    },
    () => {
      if (mounted) schedule(pipe, registryOf(pipe), joinedKey)
    },
  )

  const candidates = () => membersOf(registryOf(pipe), joinedKey).map((m) => m.candidate())

  return {
    usesPriorities: () => usesPriorities(candidates()),
    picked: (value?: unknown) => {
      if (!joinedKey) return
      const registry = registryOf(pipe)
      registry.autoPicks.delete(joinedKey)
      if (joinedKey !== SELECTED_OFFER_KEY || !isOfferGroup(value)) return
      const members = membersOf(registry, joinedKey)
      const ranked = members.map((m) => m.candidate())
      if (!usesPriorities(ranked)) return
      // Offer Select marks a pick of its default option as the default plan;
      // here the default is the card the priorities would preselect.
      pipe.setData(SELECTED_OFFER_IS_DEFAULT, members[preselectWinner(ranked)] === member)
    },
  }
}
